import type { QuoteStep } from '../types/quote.types';

/**
 * Issue #825, D6. One module, one source for both the vehicle step gate and the
 * reason shown beside the disabled Next/Complete controls. `isStepValid` is
 * DEFINED IN TERMS OF the reason in `QuoteContext.tsx`
 * (`stepBlockReason(step) === null`), so a step that is blocked always has a
 * reason and a step with a reason is always blocked — they cannot drift apart.
 *
 * Pure: no React, no store, no axios. `useQuoteSteps.ts` imports `STEP_TITLE`
 * so the step titles shown in the stepper and named in the completion message
 * cannot drift either.
 */

/** Display titles, one per step. */
export const STEP_TITLE: Record<QuoteStep, string> = {
    dealer: 'Dealer',
    vehicle: 'Vehicle',
    product: 'Product',
    configuration: 'Configure',
    customer: 'Customer',
    payment: 'Payment',
    summary: 'Summary',
};

/**
 * What each step is waiting for, as a NOUN PHRASE, so one string composes into
 * both `nextBlockedMessage` and `completionBlockedMessage`. `Record<QuoteStep,
 * string>` is deliberate: `tsc` fails the build if a step is ever added to
 * `QuoteStep` without matching copy here, and `npm run build` is the only
 * CI-enforced frontend gate (#860).
 */
export const STEP_NEEDS: Record<QuoteStep, string> = {
    dealer: 'a selected dealer',
    vehicle: 'a selected vehicle',
    product: 'a selected product',
    configuration: 'at least one configured service',
    customer: 'a selected customer',
    payment: 'a confirmed payment schedule',
    summary: 'every prior step to be complete',
};

/**
 * The vehicle step's SECOND reason: a vehicle is selected, but it is not yet
 * finished — no usable `date_of_first_registration`. Distinct from
 * `STEP_NEEDS.vehicle` (no vehicle selected at all).
 *
 * D3a. Carries a #819-style recovery sentence because #859's S3 population — a
 * stored document that is dateless AND out-of-schema on vin/vrm/lastMotDate —
 * can leave this message on screen even after a date has been visibly filled
 * in (the Redux half never opens because the auto-submit effect that would
 * write it is gated on whole-schema `isValid`). The copy must not promise that
 * a date is the only thing missing.
 */
export const VEHICLE_NEEDS_REGISTRATION_DATE =
    'a first registration date for this vehicle. If the date is filled in and this message ' +
    'stays, clear the selected vehicle above and select it again';

/** The payment step's SECOND reason: a selection exists, but the on-screen preview has not settled. */
export const PAYMENT_NEEDS_SETTLED_PREVIEW = 'the payment schedule preview to finish loading';

/** What `reportPaymentPreviewBlocked` carries (#1643): distinct states get distinct copy below. */
export type PaymentPreviewBlockReason =
    | 'unsettled'
    | 'all-methods-unavailable'
    | 'method-unoffered'
    | 'past-service-date';

/** The payment step's THIRD reason: every offered method is unavailable; the remedy is on Configure. */
export const PAYMENT_NEEDS_OFFERABLE_METHOD =
    'a scheduling method this basket can use. Go back to the Configure step and change the ' +
    'basket so a method becomes available';

/** The payment step's FOURTH reason: the persisted selection names a method no longer offered. */
export const PAYMENT_NEEDS_OFFERED_METHOD =
    'a scheduling method this product still offers. Choose one of the methods listed above';

/** The payment step's FIFTH reason: a priced service falls due before it could be collected,
 *  so no valid schedule exists; the remedy is to re-date the services on the Configure step. */
export const PAYMENT_NEEDS_FUTURE_SERVICE_DATES =
    'service dates that are not in the past. Go back to the Configure step and update the ' +
    'service dates so the first payment falls on or before each service';

/** Configuration copy is composed from the product's `minimumConfiguredServices` when `min > 1`;
 *  otherwise `STEP_NEEDS.configuration` applies, including the unreadable-bounds fallback. */
export const configurationNeeds = (min: number): string =>
    min > 1 ? `at least ${min} configured services` : STEP_NEEDS.configuration;

/** Distinct copy for an unsettled price and a failed one, so the recovery
 *  action the salesperson needs (wait vs. Retry) is named correctly. */
export const CONFIGURATION_NEEDS_CURRENT_PRICE = 'the basket price to finish calculating';
export const CONFIGURATION_NEEDS_PRICING =
    'a basket price. Pricing is unavailable; use Retry on the Configure step';

/** A session refusal has no Retry affordance, so naming one would misdirect. The copy
 *  stays neutral on cause: an expired session and a missing one share this reason. */
export const CONFIGURATION_NEEDS_REGENERATED_PLAN =
    'a basket price. This service plan session is no longer available; generate the plan again ' +
    'on the Configure step';

/** `Next` is blocked on the CURRENT step. */
export const nextBlockedMessage = (needs: string): string => `This step needs ${needs}.`;

/** `Complete Quote` is blocked on an EARLIER step; name the destination. */
export const completionBlockedMessage = (step: QuoteStep, needs: string): string =>
    `This quote cannot be completed yet. Go back to the ${STEP_TITLE[step]} step, which needs ${needs}.`;

/**
 * #931. The earliest step before `target` in the resolved order that is not yet valid, or `null`
 * when `target` is reachable.
 *
 * THE INVARIANT THIS EXISTS FOR: everything before the returned step is valid by construction, so
 * `canProceedToStep(returned)` is true and `goToStep(returned)` always navigates. A control built
 * on this answer cannot be dead — which is the whole point of #931.
 *
 * `isValid` is injected rather than imported so this stays pure: the real predicate
 * (`QuoteContext`'s `isStepValid`) needs live Redux quote data and session-scoped form state.
 */
export const firstIncompleteStepBefore = (
    order: readonly QuoteStep[],
    target: QuoteStep,
    isValid: (step: QuoteStep) => boolean
): QuoteStep | null => {
    const index = order.indexOf(target);
    if (index <= 0) return null;
    return order.slice(0, index).find((s) => !isValid(s)) ?? null;
};

/** #931. A JUMP to `target` is refused because an EARLIER step is incomplete. Name both. */
export const navigationBlockedMessage = (blockingStep: QuoteStep, target: QuoteStep): string =>
    `You cannot go to the ${STEP_TITLE[target]} step yet — the ${STEP_TITLE[blockingStep]} step is not complete.`;

/** #931. Label for a control that routes the user to the step that must be fixed first. */
export const goToStepLabel = (step: QuoteStep): string => `Go to the ${STEP_TITLE[step]} step`;

/** #1738. The step `Next` advances to, or `null` at the end of the order (or off it). */
export const stepAfter = (order: readonly QuoteStep[], current: QuoteStep): QuoteStep | null => {
    const index = order.indexOf(current);
    return index >= 0 && index < order.length - 1 ? order[index + 1] : null;
};

/** #1738. Why `Next` refuses. It advances to a TARGET, so every step before that target counts,
 *  not just the one on screen — and an earlier blocker is NAMED, since `reasonFor` would not. */
export const advanceBlockedMessage = (
    order: readonly QuoteStep[],
    current: QuoteStep,
    isValid: (step: QuoteStep) => boolean,
    reasonFor: (step: QuoteStep) => string | null
): string | null => {
    const target = stepAfter(order, current);
    if (target === null) return reasonFor(current);
    const blocker = firstIncompleteStepBefore(order, target, isValid);
    if (blocker === null) return null;
    return blocker === current ? reasonFor(current) : navigationBlockedMessage(blocker, target);
};
